/**
 * Sprint V19 · Schicht 1 (Boardroom-CoS Mo 07.09.2026, 13:00 Europe/Berlin)
 * =========================================================================
 * FREE Simple Engine — DETERMINISTISCHE Umbaustufe.
 *
 * Ersetzt vollstaendig die vorherige LLM/Heuristik-basierte Free-Engine.
 * Nur ein Eingabefeld, ein Submit-Button, ein deterministischer Output:
 *   1) Situations-Spiegelung
 *   2) Genereller Tipp (rotierend nach Input-Hash)
 *   3) Pro-Upgrade-CTA mit Tueroeffner-Wert
 *
 * KEINE Kontext-Karten. KEINE Verdacht-Box. KEINE Praezisierung.
 * KEINE Rueckfrage. KEIN Star-Modell-Helfer.
 *
 * Credits-Counter (3/Tag, UTC-Reset) bleibt sichtbar unter der Textarea.
 * Ruft POST /api/anliegen/simple auf (siehe backend/simple_engine_v19.py).
 */
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import AnliegenCreditCounter from "@/components/AnliegenCreditCounter";
import BegegnungVisual from "@/components/begegnung/BegegnungVisual";
import { FreeTypKarte, FreeTippKarte, FreeUnklarHinweis } from "@/components/redesign/FreeAnliegenErgebnis";
import { SafetyNotice } from "@/components/SafetyNotice";
import { KernAntwort } from "@/components/KernAntwort";
import { ProgressHint } from "@/components/ProgressHint";
import { sitzungsKennung } from "@/lib/sitzung";

const MAX_LEN = 1000;

export default function FreeAnliegenFlow() {
  const [situation, setSituation] = useState("");
  const [ergebnis, setErgebnis] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [creditRefreshKey, setCreditRefreshKey] = useState(0);
  const [creditLimitReached, setCreditLimitReached] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (situation.trim().length < 3) {
      setError(
        "Bitte beschreibe deine Situation kurz — mindestens ein paar Worte."
      );
      return;
    }
    setError(null);
    setLoading(true);
    try {
      const res = await api.post("/anliegen/simple", {
        situation: situation.trim(),
      }, { headers: { "X-Sitzung": sitzungsKennung() } });
      setErgebnis(res.data);
      setCreditRefreshKey((k) => k + 1);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 429) {
        setCreditLimitReached(true);
        setCreditRefreshKey((k) => k + 1);
      } else {
        setError(
          err?.response?.data?.detail?.message ||
            err?.response?.data?.detail ||
            "Antwort konnte nicht erstellt werden. Bitte in einem Moment nochmal versuchen."
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setErgebnis(null);
    setError(null);
    // situation bewusst erhalten — User kann seine Formulierung weiterentwickeln.
  };

  const handleFreshStart = () => {
    setErgebnis(null);
    setSituation("");
    setError(null);
  };

  return (
    <section
      data-testid="free-anliegen-flow"
      data-engine="v24-free"
      className="space-y-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg p-6 shadow-sm"
    >
      <AnliegenCreditCounter
        refreshKey={creditRefreshKey}
        onLimitReached={() => setCreditLimitReached(true)}
      />

      <div
        data-testid="free-anliegen-intro"
        className="bg-blue-50 dark:bg-blue-950/40 border border-blue-100 dark:border-blue-800/60 rounded-md p-4"
      >
        <p className="text-sm font-semibold text-blue-900 dark:text-blue-100">
          Was willst du erreichen?
        </p>
        <p className="mt-1 text-sm text-slate-700 dark:text-slate-200 leading-relaxed">
          Beschreib kurz deine Situation und wie dein Gegenüber reagiert. Du
          bekommst seinen Fokustyp plus einen Tipp, den du direkt ausprobieren kannst.
        </p>
        <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
          Free: 3 Credits pro Tag · Fokustyp + ein Tipp ·{" "}
          <b className="text-blue-800 dark:text-blue-300">
            Pro Solo (39 €/Monat):
          </b>{" "}
          Volle Tiefe – weitere Ansätze, Mischtypen, Tonalität und konkrete Formulierungen.
        </p>
      </div>

      {!ergebnis && (
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label
              htmlFor="free-anliegen-textarea"
              className="block text-sm font-semibold text-slate-700 dark:text-slate-200 mb-2"
            >
              Deine Situation
            </label>
            <textarea
              id="free-anliegen-textarea"
              data-testid="free-anliegen-textarea"
              value={situation}
              onChange={(e) => setSituation(e.target.value.slice(0, MAX_LEN))}
              rows={6}
              maxLength={MAX_LEN}
              placeholder="Zum Beispiel: Mein Kollege gibt mir nie direktes Feedback und ich weiß nie, woran ich bei ihm bin ..."
              className="w-full rounded-md border border-slate-300 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100 px-3 py-2 text-sm leading-relaxed focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <p
              data-testid="free-anliegen-charcount"
              className="mt-1 text-xs text-slate-400 text-right"
            >
              {situation.length} / {MAX_LEN}
            </p>
          </div>

          {error && (
            <p
              data-testid="free-anliegen-error"
              className="text-sm text-red-600 dark:text-red-400"
            >
              {error}
            </p>
          )}

          {creditLimitReached && (
            <div
              data-testid="free-anliegen-limit"
              className="rounded-md border border-amber-200 dark:border-amber-800/60 bg-amber-50 dark:bg-amber-950/40 p-4 text-sm text-amber-900 dark:text-amber-100"
            >
              <p className="font-semibold">
                Du hast deine 3 Credits für heute genutzt.
              </p>
              <p className="mt-1 text-xs text-amber-800/90 dark:text-amber-200/90">
                Morgen früh gibt es 3 neue. Oder freischalten →
              </p>
              <Link
                to="/dashboard/abo"
                data-testid="free-anliegen-limit-upgrade"
                data-testid-alias="free-anliegen-upgrade-cta"
                className="mt-3 inline-flex items-center gap-2 rounded-xl bg-[var(--fts-primary-bright)] hover:opacity-90 text-white font-bold text-sm px-5 py-2.5 transition-opacity"
              >
                Pro Solo ansehen → 39 €/Monat
              </Link>
            </div>
          )}

          <button
            type="submit"
            data-testid="free-anliegen-submit"
            disabled={
              loading ||
              creditLimitReached ||
              situation.trim().length < 3
            }
            className="inline-flex items-center gap-2 rounded-xl bg-[var(--fts-primary-deep)] hover:opacity-90 disabled:opacity-50 text-white font-bold px-6 py-3 text-sm transition-opacity"
          >
            {loading ? "Antwort wird erstellt …" : "Antwort erhalten"}
          </button>
          {loading && <ProgressHint />}
        </form>
      )}

      {ergebnis && (
        <div data-testid="free-anliegen-result" className="space-y-4">
          <BegegnungVisual begegnung={ergebnis.begegnung} isPro={false} />
          {/* Spiegelung */}
          {ergebnis.spiegelung && (
            <div
              data-testid="free-anliegen-spiegelung"
              className="bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700 rounded-md p-5"
            >
              <p className="text-xs font-bold uppercase tracking-widest text-slate-500 dark:text-slate-400 mb-2">
                Spiegelung
              </p>
              <p className="text-sm text-slate-800 dark:text-slate-100 leading-relaxed">
                {ergebnis.spiegelung}
              </p>
            </div>
          )}

          {ergebnis.status === "sicherheit" ? (
            <SafetyNotice data={ergebnis} />
          ) : ergebnis.antwort ? (
            <>
              <KernAntwort antwort={ergebnis.antwort} typLabel={ergebnis.fokustyp_name} />
              <a href="/dashboard/abo" className="inline-flex min-h-[44px] items-center rounded-full bg-[#4563BB] px-5 text-sm font-semibold text-white" data-testid="free-kern-upgrade-link">
                Pro Solo ansehen →
              </a>
            </>
          ) : ergebnis.status === "unklar" ? (
            <FreeUnklarHinweis hinweis={ergebnis.hinweis} />
          ) : (
            <>
              <FreeTypKarte ergebnis={ergebnis} />
              {ergebnis.tipp && <FreeTippKarte tipp={ergebnis.tipp} />}
            </>
          )}

          {/* Upgrade-CTA */}
          {ergebnis.upgrade_cta && (
            <div
              data-testid="free-anliegen-upgrade"
              className="rounded-md border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/40 p-5"
            >
              <p className="text-sm font-bold text-slate-900 dark:text-white">
                {ergebnis.upgrade_cta.headline}
              </p>
              <p className="mt-1 text-sm text-slate-700 dark:text-slate-200 leading-relaxed">
                {ergebnis.upgrade_cta.body}
              </p>
              <Link
                to={ergebnis.upgrade_cta.target || "/dashboard/abo"}
                data-testid="free-anliegen-upgrade-cta"
                className="mt-3 inline-flex items-center gap-2 rounded-xl bg-[var(--fts-primary-bright)] hover:opacity-90 text-white font-bold text-sm px-5 py-2.5 transition-opacity"
              >
                {ergebnis.upgrade_cta.label || "Pro Solo ansehen → 39 €/Monat"}
              </Link>
            </div>
          )}

          <div className="flex flex-wrap gap-3">
            <button
              type="button"
              onClick={handleReset}
              data-testid="free-anliegen-reset"
              className="text-sm text-slate-500 hover:text-slate-900 dark:hover:text-white underline underline-offset-2"
            >
              ← Anliegen bearbeiten
            </button>
            <button
              type="button"
              onClick={handleFreshStart}
              data-testid="free-anliegen-fresh"
              className="text-sm text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 underline underline-offset-2"
            >
              Neu anfangen
            </button>
          </div>
        </div>
      )}
    </section>
  );
}
